// App.js
import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Route, Routes, useNavigate, Navigate, useLocation } from "react-router-dom";
import { useLogout } from './hooks/useLogout';
import { useAuthContext } from './hooks/useAuthContext';
import { useTheme } from './ThemeContext';

import AuthHome from "./auth/home";
import AuthLogin from "./auth/login";
import AuthVerify from "./auth/Verify";
import AuthRegister from "./auth/register";
import PublicLayout from "./PublicLayout";
import Resume from "./pages/ResumeBuilder/resumeBuild";
import ResumeEditor from "./pages/ResumeBuilder/resumeEditor";
import ResumeLibrary from "./pages/ResumeLibrary/resumeLibrary";
import Profile from "./pages/Profile/profile";
import Settings from "./pages/Settings/settings";
import Pricing from "./pages/Pricing/pricing";
import PrivacyPolicy from './auth/privacypolicy';
import TermsandConditions from './auth/termsandconditions';
import ForgotPassword from "./auth/ForgotPassword";
import AppTopbar from "./components/appTopbar";
import { isPublicLightRoute } from "./config/publicRoutes";

const RequireAuth = ({ user, children }) => {
  if (!user) return <Navigate to="/login" />;
  return children;
};

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthReady } = useAuthContext();
  const { logout } = useLogout();
  const { darkMode, setDarkMode } = useTheme();

  const handleLogout = () => {
    logout();
    navigate('/login');
  }

  const shouldShowTopbar = () => {
    if (location.pathname.startsWith("/app/editor")) return false;
    const appPaths = ["/app/resume", "/app/profile", "/app/settings", "/app/resumes"];
    return appPaths.some(path => location.pathname.startsWith(path));
  };

  const forceLight = isPublicLightRoute(location.pathname);
  const effectiveDarkMode = forceLight ? false : darkMode;

  useEffect(() => {
    const storedDarkMode = localStorage.getItem('darkMode') === 'true';
    setDarkMode(storedDarkMode);
  }, []);

  useEffect(() => {
    document.body.classList.remove('dark-mode', 'light-mode');
    document.body.classList.add(effectiveDarkMode ? 'dark-mode' : 'light-mode');
  }, [effectiveDarkMode]);

  if (!isAuthReady) {
    return null;
  }

  return (
    <div className={`app ${effectiveDarkMode ? "dark-mode" : "light-mode"}`}>
      {user && shouldShowTopbar() && <AppTopbar />}
      <div className="content" style={user && shouldShowTopbar() ? { } : {}}>
        <Routes>
          <Route path="/" element={<PublicLayout><AuthHome /></PublicLayout>} exact />
          <Route path="/login" element={<AuthLogin />} />
          <Route path="/register" element={<AuthRegister />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/verify" element={<AuthVerify />} />
          <Route path="/app/resume" element={<RequireAuth user={user}><Resume /></RequireAuth>} />
          <Route path="/app/profile" element={<RequireAuth user={user}><Profile /></RequireAuth>} />
          <Route path="/app/editor/:resumeId" element={<RequireAuth user={user}><ResumeEditor /></RequireAuth>} />
          <Route path="/app/resumes" element={<RequireAuth user={user}><ResumeLibrary /></RequireAuth>} />
          <Route path="/app/settings" element={<RequireAuth user={user}><Settings /></RequireAuth>} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/privacypolicy" element={<PublicLayout><PrivacyPolicy /></PublicLayout>} />
          <Route path="/termsandconditions" element={<PublicLayout><TermsandConditions /></PublicLayout>} />
        </Routes>
      </div>
    </div>
  );
}

export default App;